import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ModalAnimal from '../components/ui/ModalAnimal';
import ShareButton from '../components/ui/ShareButton';
import './Animales.css';

const BADGE = { reportado:'badge-gold', rescate:'badge-blue', tratamiento:'badge-r', adopcion:'badge-green', adoptado:'badge-admin' };
const LABEL = { reportado:'📢 Reportado', rescate:'🦺 Rescatado', tratamiento:'🏥 Tratamiento', adopcion:'🏠 En adopción', adoptado:'🎒 Adoptado' };

export default function Animales() {
  const { esMaestro, esAdmin, logueado } = useAuth();
  const [animales, setAnimales] = useState([]);
  const [filtro, setFiltro]     = useState('todos');
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);

  const cargar = () => {
    setLoading(true);
    const params = {};
    if (filtro === 'perro' || filtro === 'gato') params.especie = filtro;
    else if (['reportado','rescate','tratamiento','adopcion','adoptado'].includes(filtro)) params.estado = filtro;
    api.get('/animales', { params })
      .then(r => setAnimales(r.data))
      .catch(() => toast.error('Error cargando animales'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [filtro]); // eslint-disable-line

  const adoptar = async (id, nombre) => {
    if (!logueado) { toast.error('Inicia sesión para solicitar adopción'); return; }
    try {
      const { data } = await api.post(`/animales/${id}/adoptar`);
      toast((t) => (
        <span>
          <b>¡Interés registrado!</b> 🐾<br/>
          Contacta a <b>{data.contacto.nombre}</b> al:<br/>
          <span className="text-[16px] text-v font-extrabold">📞 {data.contacto.telefono}</span>
        </span>
      ), { duration: 8000 });
      cargar();
    } catch (err) { 
      const msg = err.response?.data?.msg || 'Error';
      toast.error(msg); 
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este animal?')) return;
    try {
      await api.delete(`/animales/${id}`);
      toast.success('Animal eliminado');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const [selAnimal, setSelAnimal] = useState(null);

  const filtros = [
    { key:'todos', lbl:'Todos' },
    { key:'perro', lbl:'Perros' },
    { key:'gato',  lbl:'Gatos' },
    { key:'reportado', lbl:'📢 Reportados' },
    { key:'rescate',lbl:'🦺 Rescatados' },
    { key:'tratamiento', lbl:'🏥 Tratamiento' },
    { key:'adopcion', lbl:'🏠 Adopción' },
    { key:'adoptado', lbl:'🎒 Historias de Éxito' },
  ];

  return (
    <div className="page-wrap fade-up">
      <div className="sec-title flex justify-between">
        <span>🐾 Catálogo de animales</span>
        {logueado && (
          <button className="btn btn-v btn-sm" onClick={() => setShowModal(true)}>
            {esAdmin || esMaestro ? '+ Agregar animal' : '+ Proponer para adopción'}
          </button>
        )}
      </div>

      <div className="filtros">
        {filtros.map(f => (
          <button key={f.key} className={`filtro${filtro===f.key?' on':''}`} onClick={() => setFiltro(f.key)}>{f.lbl}</button>
        ))}
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="grid-3 animate-stagger">
          {animales.map(a => (
            <div key={a._id} className="card a-card fade-up">
              <div className="a-img-box" style={{ background: a.colorBg }}>
                {a.fotos && a.fotos.length > 0 ? (
                  <img src={a.fotos[0]} alt={a.nombre} className="a-photo" />
                ) : (
                  <span className="a-emoji">{a.icono}</span>
                )}
                <div className={`a-badge-float badge ${BADGE[a.estado]}`}>{LABEL[a.estado]}</div>
                {!a.validado && (
                  <div className="a-badge-pending badge badge-r top-[50px]">⏳ EN REVISIÓN</div>
                )}
              </div>
              <div className="a-body">
                <div className="a-header">
                  <h3 className="a-title">{a.nombre}</h3>
                  <div className="a-meta">{a.especie} · {a.edad}</div>
                </div>
                <div className="a-details">
                  <span>📏 {a.tamano}</span>
                  {a.ubicacion && <span>📍 {a.ubicacion}</span>}
                </div>
                {a.historia && <p className="a-excerpt">{a.historia.substring(0, 80)}...</p>}
                
                <div className="a-footer">
                  <button className="btn btn-ghost a-grow" onClick={() => setSelAnimal(a)}>
                    Ver perfil
                  </button>
                  {a.validado && a.publicId && (
                    <ShareButton
                      tipo="mascota"
                      publicId={a.publicId}
                      title={`${a.nombre} — Red Cimarrona`}
                      text={`Conoce a ${a.nombre} en Red Cimarrona`}
                      data={{
                        nombre: a.nombre,
                        especie: a.especie,
                        edad: a.edad,
                        tamano: a.tamano,
                        ubicacion: a.ubicacion,
                        historia: a.historia
                      }}
                      label="Compartir"
                    />
                  )}
                  {a.estado === 'adopcion' && (
                    <button className="btn btn-v a-grow-15" onClick={() => adoptar(a._id, a.nombre)}>
                      🐾 Adoptar
                    </button>
                  )}
                  {esMaestro && (
                    <button className="btn btn-r btn-sm btn-icon-only" title="Eliminar" onClick={() => eliminar(a._id)}>🗑️</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {animales.length === 0 && (
            <div className="empty-state">
              <div className="empty-ico">🐕</div>
              <p>No encontramos animales con estos filtros.</p>
              <button className="btn btn-ghost" onClick={() => setFiltro('todos')}>Ver todos</button>
            </div>
          )}
        </div>
      )}

      {showModal && <ModalAnimal onClose={() => setShowModal(false)} onCreado={cargar} />}
      {selAnimal && <ModalAnimal animal={selAnimal} onClose={() => setSelAnimal(null)} />}
    </div>
  );
}
